/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useState, useEffect } from 'react';

import RailLeft from '@react/react-spectrum/Icon/RailLeft';
import Button from '@react/react-spectrum/Button';
import { Grid, GridRow, GridColumn } from '@react/react-spectrum/Grid';
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger';
import Tooltip from '@react/react-spectrum/Tooltip';
import { GridView, GridLayout } from '@react/react-spectrum/GridView';
import Wait from '@react/react-spectrum/Wait';
import IllustratedMessage from '@react/react-spectrum/IllustratedMessage/js/IllustratedMessage';
import Alert from '@react/react-spectrum/Icon/Alert';
import { defineMessages, useIntl } from 'react-intl';

import TopBar from './TopBar';
import Breadcrumb from './Breadcrumb';
import { useProductAdminContext } from './ProductAdminContext';
import { renderItem, renderEmptyView } from './itemRendering';
import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import ViewSelector from './ViewSelector';
import Sorting from './Sorting';
import Sidebar from './Sidebar';
import { trackPage } from '../../utils/tracking';
import { product_admin_page } from '../../utils/trackingPages';

import classes from './ProductAdmin.scss';

const messages = defineMessages({
    filters: {
        id: 'sidebar.filters',
        defaultMessage: 'Filters'
    },
    openFilters: {
        id: 'sidebar.openFilters',
        defaultMessage: 'Open Filters'
    },
    closeFilters: {
        id: 'sidebar.closeFilters',
        defaultMessage: 'Close Filters'
    },
    headlineProducts: {
        id: 'headline.products',
        defaultMessage: 'Products'
    }
});

const ProductAdmin = () => {
    const [sidebar, setSidebar] = useState(false);
    const [{ dataSource, loading }] = useProductAdminContext();
    const [schemaStatus, schemaError] = useGraphqlSchemaContext();
    const { formatMessage } = useIntl();

    const isLoading = schemaStatus === 'loading' || loading;
    const hasError = schemaStatus === 'error';
    const isDone = schemaStatus === 'done' && !loading;
    let content;

    if (hasError) {
        // show an error message
        content = (
            <IllustratedMessage
                role="alert"
                heading={schemaError.title}
                description={schemaError.description}
                illustration={<Alert size="XL" />}
            />
        );
    } else if (isDone) {
        // show the content
        content = (
            <GridView
                layout={GridLayout}
                dataSource={dataSource}
                renderItem={renderItem}
                renderEmptyView={renderEmptyView}
                allowsMultipleSelection={false}
                canDragItems={false}
                acceptsDrops={false}
                allowsSelection={false}
                className={classes.gridView}
            />
        );
    } else if (isLoading) {
        content = <Wait size="L" centered />;
    } else {
        content = <></>;
    }

    const toggleSidebar = () => {
        setSidebar(!sidebar);
    };

    useEffect(() => {
        // Force size update of GridView
        window.dispatchEvent(new Event('resize'));
    }, [sidebar]);

    useEffect(() => {
        trackPage(product_admin_page);
    }, []);

    return (
        <Grid className={classes.productAdmin}>
            <GridRow className={classes.row}>
                <GridColumn className={classes.noPadding}>
                    <TopBar>
                        <div position="left">
                            <OverlayTrigger placement="right" selected={sidebar}>
                                <Button
                                    variant="tool"
                                    icon={<RailLeft />}
                                    onClick={toggleSidebar}
                                    aria-label={formatMessage(messages.filters)}
                                />
                                <Tooltip variant="info">
                                    {sidebar
                                        ? formatMessage(messages.closeFilters)
                                        : formatMessage(messages.openFilters)}
                                </Tooltip>
                            </OverlayTrigger>
                        </div>
                        <div position="right">
                            <Sorting />
                            <ViewSelector disabled />
                        </div>
                        <div position="center">
                            <Breadcrumb headline={formatMessage(messages.headlineProducts)} />
                        </div>
                    </TopBar>
                </GridColumn>
            </GridRow>
            <GridRow className={[classes.row, classes.bodyRow].join(' ')}>
                {sidebar && <Sidebar />}
                <GridColumn>{content}</GridColumn>
            </GridRow>
        </Grid>
    );
};

export default ProductAdmin;
